<template lang="html">
  <div class="group product-card relative flex flex-col border border-default">
    <div class="product-card__tools absolute top-0 left-0 z-10 w-full px-2 py-1">
      <PromoBadge
        :badge="props.value.promoBadge"
        :cart-promo="props.value.hasCartPromo && !props.value.hasCashbackPromo"
        :cashback="props.value.hasCashbackPromo"
      />
      <div
        v-if="!isInStock"
        key="isNotInStock"
        class="not-in-stock absolute top-1 left-1 bg-tertiary-500 px-2 py-1 text-sm text-white"
      >
        Нет в наличии
      </div>
      <UButton
        :icon="isFavorite ? 'i-heroicons-heart-solid' : 'i-lucide-heart'"
        :color="isFavorite ? 'error' : 'neutral'"
        variant="ghost"
        class="absolute top-1 right-1"
        @click.stop.prevent="onFavoriteToggle"
      />
    </div>

    <NuxtLink :to="link" class="relative aspect-9/10 flex-1">
      <UiPicture
        key="cover"
        class="product-card__img flex-1"
        :src="coverId"
        variant="imageFull"
        :alt="props.value.wellKnownAttributes?.Name"
        :sizes="CARD_SIZES"
        :loading="props.eager ? undefined : 'lazy'"
      />

      <!-- ховер-картинка невидима до наведения — всегда lazy -->
      <UiPicture
        v-if="sideCoverId"
        key="side-cover"
        class="product-card__img absolute top-0 right-0 h-full w-full opacity-0 transition-opacity duration-100 group-hover:opacity-100"
        :src="sideCoverId"
        variant="imageFull"
        :alt="props.value.wellKnownAttributes?.Name"
        :sizes="CARD_SIZES"
        loading="lazy"
      />
    </NuxtLink>
    <div
      class="product-card__info flex items-center justify-between gap-2 overflow-hidden border-t border-default bg-white p-4"
    >
      <div class="flex min-w-0 flex-col gap-1">
        <NuxtLink :to="link" class="truncate">
          {{ props.value.wellKnownAttributes?.Name }}
        </NuxtLink>
        <div v-if="showRating" class="flex items-center gap-1 text-xs text-muted">
          <RatingStars :value="props.value.rating" size="xs" />
          <span>{{ props.value.reviewsCount }}</span>
        </div>
      </div>
      <PromoPrice :value="props.value" class="shrink-0 text-right" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { EntityRef, ProductListItemDto } from '@repo/api/web/models'

import PromoBadge from '#Promotion/components/PromoBadge.vue'
import PromoPrice from '#Promotion/components/PromoPrice.vue'
import RatingStars from '#Reviews/components/RatingStars.vue'
import { useReviewsEnabled } from '#Reviews/composables/useReviewsConfig'
import UiPicture from '~/app/components/Image/UiPicture.vue'

const props = withDefaults(
  defineProps<{
    value: ProductListItemDto
    category?: EntityRef
    /** Первый ряд листинга (LCP-кандидаты) грузится сразу, остальные lazy */
    eager?: boolean
  }>(),
  {
    eager: false,
  },
)

/**
 * Реальная ширина слота карточки: сетка minmax(300px,1fr) в max-w-480 —
 * на десктопе ~300–480px (4–5 колонок), на широких мониторах до ~600px.
 * Без sizes браузер качал бы 640px+; слишком низкий cap → мыло на retina.
 */
const CARD_SIZES = '100vw sm:50vw lg:600px'

const { routing } = useProducts()
const { toggle: toggleFavorite } = useFavoritesCore()
const reviewsEnabled = useReviewsEnabled()

/** Звёзды и число отзывов — только при включённых отзывах и если отзывы уже есть */
const showRating = computed(() => reviewsEnabled.value && (props.value.reviewsCount ?? 0) > 0)

const coverId = computed(() => props.value.images?.[0]?.id)
const sideCoverId = computed(() => props.value.images?.[1]?.id)

const link = computed(() => routing.getDetails(props.value, props.category).route)
/** Группа вариантов: бейдж только если нет остатков ни у одного SKU */
const isInStock = computed(() => Boolean(props.value.templateInStock))
const model = ref(props.value)

const isFavorite = computed(() => model.value.isFavorite)

async function onFavoriteToggle() {
  await toggleFavorite(model.value)
}
</script>
